/* Хвост заказа «Говорящего кружка» (APP-SPEC-4 §8) на общем OrderTail: герой — кружок (circle) или рамка формата,
   тексты статуса, итога, отказа проверки и сбоя по тому, что проверялось (текст · аудио · видео); карточка .ar-q
   «кто · голос · формат · длительность», текст до четырёх строк, строка фонового звука; результат — ролик через
   авторизованный openResult (без автоматической загрузки); «Что дальше» — «Сделать видео» / «Сделать кружок», «Другой голос».
   Знак над меню — вид circle (кружок) или lipsync (видео). Медиа ответа не вставляются в плеер по внешним URL. */
import type { ReactNode } from 'react';
import { uiText } from '../../../i18n';
import { StudioIcon } from '../StudioIcon';
import type { CircleFormState } from './CircleFieldInput';
import { OrderTail, type TailAction } from './OrderTail';
import { mmss, tailDisplay } from './order-tail-model';
import { leaveTail } from './tool-helpers';
import { circleFormatWord, circleKeys, circleRatio, circlePolicy } from './circle-model';

export interface CircleTailSnapshot {
  /** Кто говорит и голос — подписи формы на момент заказа */
  who?: string;
  voice?: string;
  picture?: ReactNode;
  /** Своя речь и смена голоса возможны: «Другой голос» */
  revoice?: boolean;
  /** «Кружок из аудио» */
  audio?: boolean;
}
const text = (value: unknown) => (typeof value === 'string' ? value : '');
/** Рамка итога по пропорциям в коробке 236 × 300 — как герой статуса OrderTail */
function frameSize(ratio: number) {
  const r = Number.isFinite(ratio) && ratio > 0 ? ratio : 9 / 16;
  return r >= 236 / 300 ? { w: 236, h: Math.round(236 / r) } : { w: Math.round(300 * r), h: 300 };
}

export function CircleOrderResult({
  state,
  snapshot = {},
  onRetry,
  onFormat,
  onVoice,
}: {
  state: CircleFormState;
  snapshot?: CircleTailSnapshot;
  onRetry?(): void;
  /** «Сделать видео» / «Сделать кружок»: форма с тем же входом в другом формате */
  onFormat?(format: string): void;
  /** «Другой голос»: форма и сразу секция «Голос» */
  onVoice?(): void;
}) {
  const { orderId, order, result } = state;
  if (!orderId) return null;
  const keys = state.form?.fields ? circleKeys(state.form) : null;
  const body = (state.intent?.body?.inputs ?? state.inputs ?? {}) as Record<string, string>;
  const format = body[keys?.format ?? 'format'] || (state.form?.fields ? circlePolicy(state.form)?.default_format : '') || 'circle';
  const round = format === 'circle';
  const speechValue = keys?.speech ? body[keys.speech] : '';
  const hasText = !!(keys?.text ?? 'text') && !!body[keys?.text ?? 'text'];
  const hasAudio = !!body[keys?.audio ?? 'audio'];
  const checked: 'text' | 'audio' | 'video' = speechValue === 'own' || (!hasText && !hasAudio && !!body[keys?.video ?? 'video']) ? 'video' : hasAudio || speechValue === 'audio' ? 'audio' : 'text';
  const what = round ? 'кружок' : 'видео';
  const display = tailDisplay(order);
  const durationMs = Number(display.duration_ms ?? (Number(display.duration) * 1000 || NaN));
  const meta = [
    text(display.who) || snapshot.who,
    snapshot.audio ? '' : text(display.voice_name) || snapshot.voice,
    circleFormatWord(format) === 'Кружок' ? 'кружок' : circleFormatWord(format),
    Number.isFinite(durationMs) && durationMs > 0 ? mmss(durationMs / 1000) : '',
  ]
    .filter(Boolean)
    .join(' · ');
  const said = text(display.text) || body[keys?.text ?? 'text'] || '';
  const sfx = text(display.sfx_prompt) || (keys?.sfx ? body[keys.sfx] : '') || '';
  const asset = order?.result?.assets?.[0];
  const media = result && result.mime.startsWith('video/') ? result.url : null;
  const deliveryState = (order as { delivery?: { state?: unknown } } | null)?.delivery?.state;
  const delivered = deliveryState === 'sent' || deliveryState === 'delivered';
  const playable = !!asset && !!order?.result?.available;
  const playLabel = uiText(round ? 'Проиграть кружок' : 'Смотреть');
  const assetMs = Number((asset as { duration_ms?: unknown } | undefined)?.duration_ms ?? durationMs);
  const pill = Number.isFinite(assetMs) && assetMs > 0 ? mmss(assetMs / 1000) : '';
  const frame = frameSize(circleRatio(format));
  /* Итог до загрузки ролика (§8.3): чёткий снимок в круге или рамке, белая кнопка воспроизведения по центру, пилюля
     с длительностью. Раньше на месте героя стояла одна пилюля «Проиграть кружок» без кадра. Ролик грузится по нажатию
     (openResult, без автоматической загрузки). */
  const playFace = (
    <>
      <span className={round ? 'ar-pic' : 'pr-pic'}>
        <i>{snapshot.picture}</i>
      </span>
      <span className="ar-mid">
        <span className="pb">
          <StudioIcon kind="play" />
        </span>
      </span>
      {pill && <span className={round ? 'ar-pill' : 'ar-pill pr-pill'}>{pill}</span>}
    </>
  );
  const resultHero = media ? (
    <div className={`circle-result${round ? ' circle-result-round' : ''}`}>
      <video controls playsInline autoPlay src={media} aria-label={playLabel} />
    </div>
  ) : playable ? (
    round ? (
      <div className="ar-hero circle-result-hero">
        <button type="button" className="ar-c" disabled={state.busy} aria-label={playLabel} onClick={() => void state.openResult(asset!.index)}>
          {playFace}
        </button>
      </div>
    ) : (
      <div className="pr-hero circle-result-hero">
        <button
          type="button"
          className="pr-f circle-result-frame"
          style={{ width: frame.w, height: frame.h }}
          disabled={state.busy}
          aria-label={playLabel}
          onClick={() => void state.openResult(asset!.index)}
        >
          {playFace}
        </button>
      </div>
    )
  ) : null;
  const next: TailAction[] = [];
  const formatField = keys?.format ? state.form?.fields.find(field => field.key === keys.format) : undefined;
  if (onFormat && formatField) {
    if (round && formatField.values.includes('9:16')) next.push({ label: 'Сделать видео', icon: 'video', onClick: () => onFormat('9:16') });
    if (!round && formatField.values.includes('circle')) next.push({ label: 'Сделать кружок', icon: 'circle', onClick: () => onFormat('circle') });
  }
  if (onVoice && snapshot.revoice && !snapshot.audio) next.push({ label: 'Другой голос', icon: 'voice', onClick: onVoice });
  const modAction = checked === 'text' ? 'Изменить текст' : checked === 'audio' ? 'Заменить аудио' : 'Заменить видео';
  const modH = checked === 'text' ? 'Текст не прошёл проверку' : checked === 'audio' ? 'Аудио не прошло проверку' : 'Видео не прошло проверку';
  return (
    <>
      {state.error && <p role="alert">{uiText(state.error)}</p>}
      <OrderTail
        state={state}
        kind={round ? 'circle' : 'lipsync'}
        className="circle-tail"
        hero={{ shape: round ? 'circle' : 'frame', ratio: circleRatio(format), picture: snapshot.picture, result: resultHero }}
        texts={{
          runH: round ? 'Создаём кружок' : 'Создаём видео',
          runP: round ? 'Можно уйти с экрана: бот пришлёт готовый кружок в чат.' : 'Можно уйти с экрана: бот пришлёт готовое видео в чат.',
          doneH: round ? 'Кружок готов' : 'Видео готово',
          /* «уже прислал» — только при подтверждённой доставке: на стенде заказ готов с delivery.state = not_requested */
          /* «Бот сам не присылает» (настройки) подставляет общий OrderTail по delivery.state/reason */
          doneP: delivered ? 'Бот уже прислал его в чат, в отдельную тему.' : 'Бот пришлёт его в чат, в отдельную тему.',
          modH,
          modStep: modH,
          notMade: round ? 'кружок не создан' : 'видео не создано',
          techH: round ? 'Не получилось собрать кружок' : 'Не получилось собрать видео',
        }}
        card={
          (meta || said || sfx) && (
            <div className="ar-q">
              {meta && <small>{meta}</small>}
              {said && <p>{said}</p>}
              {sfx && (
                <span className="sf">
                  <StudioIcon kind="wave" />
                  <span>{sfx}</span>
                </span>
              )}
            </div>
          )
        }
        next={next}
        modAction={modAction}
        foot={{ mod: { label: modAction, icon: 'edit', onClick: () => void leaveTail(state) } }}
        chatTip={`Тут откроется тема чата, куда бот прислал ${what}`}
        onRetry={onRetry}
      />
    </>
  );
}
